import { frontendURL } from 'dashboard/helper/URLHelper';
import { SETTINGS_ACCESS_PERMISSIONS } from 'dashboard/constants/permissions.js';

const TelephonyDashboard = () => import('./pages/TelephonyDashboard.vue');
const TrunksList = () => import('./pages/trunks/TrunksList.vue');
const TrunkForm = () => import('./pages/trunks/TrunkForm.vue');
const ExtensionsList = () => import('./pages/extensions/ExtensionsList.vue');
const ExtensionForm = () => import('./pages/extensions/ExtensionForm.vue');
const ExternalExtensionLinks = () =>
  import('./pages/extensions/ExternalExtensionLinks.vue');
const DidsList = () => import('./pages/dids/DidsList.vue');
const DidForm = () => import('./pages/dids/DidForm.vue');
const QueuesList = () => import('./pages/queues/QueuesList.vue');
const QueueForm = () => import('./pages/queues/QueueForm.vue');
const QueueMembers = () => import('./pages/queues/QueueMembers.vue');
const QueueRoutesList = () =>
  import('./pages/queue-routes/QueueRoutesList.vue');
const QueueRouteForm = () => import('./pages/queue-routes/QueueRouteForm.vue');
const CallRecordsList = () =>
  import('./pages/call-records/CallRecordsList.vue');
const CallRecordsDashboard = () =>
  import('./pages/call-records/CallRecordsDashboard.vue');
const ActiveCallsList = () =>
  import('./pages/active-calls/ActiveCallsList.vue');
const IvrAudiosList = () => import('./pages/ivr/IvrAudiosList.vue');
const IvrBuilder = () => import('./pages/ivr/IvrBuilder.vue');
const OperationalPanel = () =>
  import('./pages/supervisor-panel/OperationalPanel.vue');
const SupervisorPanel = () =>
  import('./pages/supervisor-panel/SupervisorPanel.vue');

const meta = {
  permissions: ['administrator'],
};

// Painel, Relatório de Pausas e Histórico também ficam acessíveis pra quem
// tem a permissão de custom role telephony_reports_access — as demais
// rotas de telefonia (trunks, ramais, filas, IVR, etc.) continuam só admin.
const reportsMeta = {
  permissions: ['administrator', 'telephony_reports_access'],
};

// Painel Operacional/Supervisor: precisa ser navegável por quem tem o
// custom_role "Supervisor" (permissions vêm do custom_role, não do role base
// — ver getUserPermissions em dashboard/helper/permissionsHelper.js, que
// troca 'administrator' pela lista do custom_role quando ele existe). O
// custom_role Supervisor tem `settings_access`, então usamos essa permissão
// aqui em vez de reaproveitar o `meta` estrito das outras rotas de telefonia
// (essas eu não mexo — não sei se o admin-only ali é intencional).
// A restrição de QUEM VÊ O QUÊ dentro da tela já é feita pelo
// useSupervisorScope.js (baseado em supervisor_assignments do backend), não
// pela rota — aqui só decide quem consegue navegar até a página.
const supervisorPanelMeta = {
  permissions: ['administrator', SETTINGS_ACCESS_PERMISSIONS],
};

const telephonyRoutes = {
  routes: [
    // Dashboard
    {
      path: frontendURL('accounts/:accountId/telephony'),
      name: 'telephony_dashboard',
      meta: reportsMeta,
      component: TelephonyDashboard,
    },

    // Trunks
    {
      path: frontendURL('accounts/:accountId/telephony/trunks'),
      name: 'telephony_trunks',
      meta,
      component: TrunksList,
    },
    {
      path: frontendURL('accounts/:accountId/telephony/trunks/new'),
      name: 'telephony_trunk_new',
      meta,
      component: TrunkForm,
    },
    {
      path: frontendURL('accounts/:accountId/telephony/trunks/:trunkName/edit'),
      name: 'telephony_trunk_edit',
      meta,
      component: TrunkForm,
      props: route => ({ trunkName: route.params.trunkName }),
    },

    // Extensions
    {
      path: frontendURL('accounts/:accountId/telephony/extensions'),
      name: 'telephony_extensions',
      meta,
      component: ExtensionsList,
    },
    {
      path: frontendURL('accounts/:accountId/telephony/extensions/new'),
      name: 'telephony_extension_new',
      meta,
      component: ExtensionForm,
    },
    {
      path: frontendURL(
        'accounts/:accountId/telephony/extensions/:extensionId/edit'
      ),
      name: 'telephony_extension_edit',
      meta,
      component: ExtensionForm,
      props: route => ({ extensionId: route.params.extensionId }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/telephony/external-extension-links'
      ),
      name: 'telephony_external_extension_links',
      meta,
      component: ExternalExtensionLinks,
    },

    // DIDs
    {
      path: frontendURL('accounts/:accountId/telephony/dids'),
      name: 'telephony_dids',
      meta,
      component: DidsList,
    },
    {
      path: frontendURL('accounts/:accountId/telephony/dids/new'),
      name: 'telephony_did_new',
      meta,
      component: DidForm,
    },
    {
      path: frontendURL('accounts/:accountId/telephony/dids/:didId/edit'),
      name: 'telephony_did_edit',
      meta,
      component: DidForm,
      props: route => ({ didId: route.params.didId }),
    },

    // Queues
    {
      path: frontendURL('accounts/:accountId/telephony/queues'),
      name: 'telephony_queues',
      meta,
      component: QueuesList,
    },
    {
      path: frontendURL('accounts/:accountId/telephony/queues/new'),
      name: 'telephony_queue_new',
      meta,
      component: QueueForm,
    },
    {
      path: frontendURL('accounts/:accountId/telephony/queues/:queueName/edit'),
      name: 'telephony_queue_edit',
      meta,
      component: QueueForm,
      props: route => ({ queueName: route.params.queueName }),
    },
    {
      path: frontendURL(
        'accounts/:accountId/telephony/queues/:queueName/members'
      ),
      name: 'telephony_queue_members',
      meta,
      component: QueueMembers,
      props: route => ({ queueName: route.params.queueName }),
    },

    // Queue Routes (shortcodes #600 → fila)
    {
      path: frontendURL('accounts/:accountId/telephony/queue-routes'),
      name: 'telephony_queue_routes',
      meta,
      component: QueueRoutesList,
    },
    {
      path: frontendURL('accounts/:accountId/telephony/queue-routes/new'),
      name: 'telephony_queue_route_new',
      meta,
      component: QueueRouteForm,
    },
    {
      path: frontendURL(
        'accounts/:accountId/telephony/queue-routes/:routeId/edit'
      ),
      name: 'telephony_queue_route_edit',
      meta,
      component: QueueRouteForm,
      props: route => ({ routeId: route.params.routeId }),
    },

    // Call Records (CDR)
    {
      path: frontendURL('accounts/:accountId/telephony/call-records'),
      name: 'telephony_call_records',
      meta: reportsMeta,
      component: CallRecordsList,
    },
    {
      path: frontendURL('accounts/:accountId/telephony/call-records/dashboard'),
      name: 'telephony_call_records_dashboard',
      meta,
      component: CallRecordsDashboard,
    },

    // Active Calls (live)
    {
      path: frontendURL('accounts/:accountId/telephony/active-calls'),
      name: 'telephony_active_calls',
      meta,
      component: ActiveCallsList,
    },

    // IVR
    {
      path: frontendURL('accounts/:accountId/telephony/ivr'),
      name: 'telephony_ivr',
      meta: { ...meta, platform: 'conventional_voip' },
      component: IvrAudiosList,
    },
    {
      path: frontendURL('accounts/:accountId/telephony/ivr/builder'),
      name: 'telephony_ivr_builder_new',
      meta: { ...meta, platform: 'conventional_voip' },
      component: IvrBuilder,
    },
    {
      path: frontendURL('accounts/:accountId/telephony/ivr/builder/:flowId'),
      name: 'telephony_ivr_builder_edit',
      meta: { ...meta, platform: 'conventional_voip' },
      component: IvrBuilder,
      props: route => ({ flowId: route.params.flowId }),
    },

    // Painel Operacional/Supervisor (bespoke Aliança, conta 161 — gated pela
    // feature flag supervisor_live_panel)
    {
      path: frontendURL('accounts/:accountId/telephony/operational-panel'),
      name: 'telephony_operational_panel',
      meta: supervisorPanelMeta,
      component: OperationalPanel,
    },
    {
      path: frontendURL('accounts/:accountId/telephony/supervisor-panel'),
      name: 'telephony_supervisor_panel',
      meta: supervisorPanelMeta,
      component: SupervisorPanel,
    },
  ],
};

export default telephonyRoutes;
